import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ShieldCheck, ArrowRight, Envelope, CheckCircle, SpinnerGap, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

// PKCE Helper Functions
function generateCodeVerifier() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64URLEncode(array);
}

async function generateCodeChallenge(verifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64URLEncode(new Uint8Array(digest));
}

function base64URLEncode(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetLink, setResetLink] = useState(null);
  const [localLoginEnabled, setLocalLoginEnabled] = useState(true);
  const [ssoConfigured, setSsoConfigured] = useState(false);
  const [ssoFailed, setSsoFailed] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Fetch login page configuration on mount
  useEffect(() => {
    API.get('/features/login-config')
      .then(res => {
        setLocalLoginEnabled(res.data.local_login_enabled);
        setSsoConfigured(res.data.sso_configured);
      })
      .catch(() => {
        // Default to showing local login if config fails
        setLocalLoginEnabled(true);
      });
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await API.post('/auth/login', { email, password });
      login(res.data.user);
      toast.success('Welcome back!');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSSO = async () => {
    setSsoLoading(true);
    try {
      // Generate PKCE code verifier and challenge
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      
      // Store the code verifier for use in the callback
      sessionStorage.setItem('pkce_code_verifier', codeVerifier);
      
      const res = await API.get('/auth/sso/config');
      if (res.data.configured && res.data.auth_url) {
        // Append PKCE parameters to the auth URL
        const authUrl = new URL(res.data.auth_url);

        // Validate redirect hostname — only allow Microsoft login endpoints
        const allowedHosts = ['login.microsoftonline.com', 'login.microsoft.com', 'login.windows.net'];
        if (!allowedHosts.includes(authUrl.hostname)) {
          toast.error('SSO configuration error: untrusted redirect hostname');
          setSsoFailed(true);
          return;
        }

        authUrl.searchParams.set('code_challenge', codeChallenge);
        authUrl.searchParams.set('code_challenge_method', 'S256');
        
        window.location.href = authUrl.toString();
      } else {
        toast.error('Azure AD SSO is not configured. Please configure SSO in Admin Settings.');
        setSsoFailed(true);
      }
    } catch (err) {
      toast.error('SSO is unavailable. Use local login instead.');
      setSsoFailed(true);
    } finally {
      setSsoLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      toast.error('Please enter your email address');
      return;
    }
    setResetLoading(true);
    try {
      const res = await API.post('/auth/forgot-password', { email: resetEmail });
      setResetSent(true);
      if (res.data.reset_link) {
        // SMTP not configured — show the link directly
        setResetLink(res.data.reset_link);
        toast.success('Reset link generated (email not configured)');
      } else {
        toast.success('Password reset email sent!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setResetLoading(false);
    }
  };

  const openForgotPassword = () => {
    setResetEmail('');
    setResetSent(false);
    setResetLink(null);
    setShowForgotPassword(true);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 relative overflow-hidden items-center justify-center p-12">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 40px, rgba(255,255,255,0.03) 40px, rgba(255,255,255,0.03) 41px), repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(255,255,255,0.03) 40px, rgba(255,255,255,0.03) 41px)'
          }} />
        </div>
        <div className="relative z-10 max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-sm bg-orange-500 flex items-center justify-center">
              <ShieldCheck size={28} weight="duotone" className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-white tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                IAG Platform
              </h1>
              <p className="text-xs text-navy-500 uppercase tracking-widest">Identity Access Governance</p>
            </div>
          </div>
          <h2 className="text-4xl font-black text-white tracking-tight leading-none mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Govern Identity.<br />
            Control Access.<br />
            <span className="text-orange-500">Secure Everything.</span>
          </h2>
          <p className="text-navy-400 leading-relaxed">
            Enterprise-grade identity access governance powered by Microsoft Azure Directory.
            Manage identities, roles, entitlements, and access workflows from a single platform.
          </p>
          <div className="mt-10 flex gap-8 text-sm">
            <div>
              <p className="text-2xl font-black text-orange-500" style={{ fontFamily: 'Chivo, sans-serif' }}>100%</p>
              <p className="text-navy-500">Azure AD Native</p>
            </div>
            <div>
              <p className="text-2xl font-black text-orange-500" style={{ fontFamily: 'Chivo, sans-serif' }}>JML</p>
              <p className="text-navy-500">Workflow Engine</p>
            </div>
            <div>
              <p className="text-2xl font-black text-orange-500" style={{ fontFamily: 'Chivo, sans-serif' }}>AI</p>
              <p className="text-navy-500">Powered Search</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel - Login form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-sm bg-orange-500 flex items-center justify-center">
              <ShieldCheck size={22} weight="duotone" className="text-white" />
            </div>
            <h1 className="text-xl font-black text-navy-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
              IAG Platform
            </h1>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Sign in
            </h2>
            <p className="text-sm text-navy-500 mt-1">Access the governance platform</p>
          </div>

          {/* SSO Button — show when SSO is configured */}
          {ssoConfigured && (
            <Button
              onClick={handleSSO}
              disabled={ssoLoading}
              variant="outline"
              className="w-full mb-6 h-11 border-slate-200 hover:bg-slate-50 text-sm font-medium"
              data-testid="sso-login-button"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 21 21" fill="none">
                <path d="M0 0h10v10H0z" fill="#F25022"/>
                <path d="M11 0h10v10H11z" fill="#7FBA00"/>
                <path d="M0 11h10v10H0z" fill="#00A4EF"/>
                <path d="M11 11h10v10H11z" fill="#FFB900"/>
              </svg>
              {ssoLoading ? 'Connecting...' : 'Sign in with Azure AD'}
            </Button>
          )}

          {/* Local login form — show when enabled OR when SSO fails as fallback */}
          {(localLoginEnabled || ssoFailed) && (
            <>
              {ssoConfigured && (
                <div className="relative mb-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-slate-50 px-3 text-navy-400 font-semibold tracking-wider">
                      {ssoFailed ? 'SSO unavailable — use admin login' : 'or admin login'}
                    </span>
                  </div>
                </div>
              )}

              {ssoFailed && !localLoginEnabled && (
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mb-4 text-xs text-amber-700 flex items-center gap-2" data-testid="sso-fallback-notice">
                  <WarningCircle size={16} className="shrink-0" />
                  <span>SSO is unavailable. Local login has been temporarily enabled.</span>
                </div>
              )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs font-semibold text-navy-600 uppercase tracking-wider">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-10 rounded-sm border-slate-200 focus:ring-orange-500/20 focus:border-orange-500"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-semibold text-navy-600 uppercase tracking-wider">
                  Password
                </Label>
                <button 
                  type="button" 
                  onClick={openForgotPassword}
                  className="text-xs text-orange-500 hover:text-orange-600 font-medium"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-10 rounded-sm border-slate-200 focus:ring-orange-500/20 focus:border-orange-500"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-10 bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-medium shadow-sm active:scale-[0.98] transition-all"
              data-testid="login-submit-button"
            >
              {loading ? 'Signing in...' : 'Sign in'}
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </form>
          </>
          )}

          {/* SSO-only mode: show helpful text when local login is hidden */}
          {!localLoginEnabled && !ssoFailed && (
            <p className="text-center text-xs text-navy-400 mt-4">
              Local login is disabled. Sign in using Azure AD above.
            </p>
          )}

          <p className="mt-6 text-center text-xs text-navy-400">
            Contact your administrator if you need access
          </p>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              {resetSent ? 'Check Your Email' : 'Reset Password'}
            </DialogTitle>
          </DialogHeader>
          
          {resetSent ? (
            <div className="py-4 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-emerald-600" />
              </div>
              {resetLink ? (
                <>
                  <p className="text-sm text-navy-600 mb-3">
                    Email is not configured. Use the link below to reset the password:
                  </p>
                  <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 mb-3 break-all">
                    <a href={resetLink} className="text-xs text-orange-600 hover:text-orange-700 underline font-mono" data-testid="reset-link-direct">
                      {resetLink}
                    </a>
                  </div>
                  <p className="text-xs text-navy-400">
                    This link expires in 30 minutes.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm text-navy-600 mb-2">
                    If an account exists for <strong>{resetEmail}</strong>, you will receive a password reset link shortly.
                  </p>
                  <p className="text-xs text-navy-400">
                    The link will expire in 30 minutes.
                  </p>
                </>
              )}
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4 py-2">
              <div className="text-center mb-4">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-3">
                  <Envelope size={24} weight="duotone" className="text-orange-600" />
                </div>
                <p className="text-sm text-navy-500">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Email Address</Label>
                <Input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="mt-1 rounded-sm"
                  placeholder="admin@iag.local"
                  data-testid="reset-email-input"
                />
              </div>
              <DialogFooter className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowForgotPassword(false)} className="rounded-sm flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={resetLoading} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm flex-1" data-testid="send-reset-email-button">
                  {resetLoading ? <><SpinnerGap size={14} weight="bold" className="mr-1 animate-spin" /> Sending...</> : 'Send Reset Link'}
                </Button>
              </DialogFooter>
            </form>
          )}
          
          {resetSent && (
            <DialogFooter>
              <Button onClick={() => setShowForgotPassword(false)} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm">
                Back to Login
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
